import React, { useState, useCallback, useMemo } from 'react';
import {
  FiBookOpen,
  FiFileText,
  FiDatabase,
  FiFolder
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import LessonPlanManager from './components/LessonPlanManager';
import AssignmentManager from './components/AssignmentManager';
import StudyMaterialManager from './components/StudyMaterialManager';

const LMSView = ({ classItem, subject, labBatch, facultyId, isActive }) => {
  const [activeTab, setActiveTab] = useState('study-materials');
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs = [
    { id: 'study-materials', label: 'Study Materials', icon: FiFolder },
    { id: 'lesson-plans', label: 'Lesson Plans', icon: FiBookOpen },
    { id: 'assignments', label: 'Assignments', icon: FiFileText },
    { id: 'question-bank', label: 'Question Bank', icon: FiDatabase },
  ];

  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Only fetch data when tab is active
  const isTabActive = useMemo(() => isActive, [isActive]);

  if (!isTabActive) {
    return (
      <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
        <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300 animate-pulse">
          <FiBookOpen className="w-5 h-5" />
        </div>
        <p className="text-xs font-semibold text-slate-400 tracking-wide">Opening Portal Classroom...</p>
      </div>
    );
  }

  const renderContent = () => {
    const props = {
      classItem,
      subject,
      labBatch,
      facultyId,
      refreshKey,
      onRefresh: handleRefresh
    };

    switch (activeTab) {
      case 'study-materials':
        return <StudyMaterialManager {...props} />;
      case 'lesson-plans':
        return <LessonPlanManager {...props} />;
      case 'assignments':
        return <AssignmentManager {...props} />;

      default:
        return null;
    }
  };

  return (
    <div className="w-full space-y-5">
      {/* Sub Tabs Bar Container */}
      <div className="w-full border-b border-slate-200/80 bg-white pb-1 overflow-x-auto no-scrollbar scroll-smooth flex items-center">
        <div className="flex gap-1.5 px-0.5 whitespace-nowrap">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all uppercase tracking-wide border ${
                  isSelected
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-100'
                    : 'bg-white border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-slate-400'}`} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Feature Content Screen */}
      <div className="min-h-[420px] relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-${refreshKey}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="w-full"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LMSView;